export interface Template {
  slug: string;
  icon: string;
  category: 'finance' | 'planning' | 'hr' | 'business' | 'marketing';
  nameKey: string;
  descKey: string;
  seoTitleKey: string;
  seoDescKey: string;
  sheetData: any[];
  sheetDataRu?: any[];
}

/* ── helpers ─────────────────────────────────────────── */

const hdr = (r: number, c: number, v: string) => ({
  r, c,
  v: { v, m: v, ct: { fa: 'General', t: 'g' }, bl: 1, bg: '#4CAF50', fc: '#FFFFFF' },
});

const cell = (r: number, c: number, v: string | number, type: 'g' | 'n' = 'g') => ({
  r, c,
  v: { v, m: String(v), ct: { fa: 'General', t: type } },
});

const formula = (r: number, c: number, f: string) => ({
  r, c,
  v: { f, v: 0, m: '0', ct: { fa: 'General', t: 'n' } },
});

const colw = (widths: number[]) =>
  widths.map((w, i) => ({ cix: i, width: w }));

function sheet(name: string, celldata: any[], columnWidths: number[], rowCount = 30, colCount = 26) {
  return [{
    name,
    status: 1,
    celldata,
    config: { columnlen: Object.fromEntries(columnWidths.map((w, i) => [i, w])) },
    row: rowCount,
    column: colCount,
  }];
}

/* ══════════════════════════════════════════════════════
   1. Monthly Budget
   ══════════════════════════════════════════════════════ */
const budgetCells = [
  hdr(0, 0, 'Category'), hdr(0, 1, 'Planned'), hdr(0, 2, 'Actual'), hdr(0, 3, 'Difference'),
  cell(1, 0, 'Housing'), cell(1, 1, 1500, 'n'), cell(1, 2, 1520, 'n'), formula(1, 3, '=B2-C2'),
  cell(2, 0, 'Groceries'), cell(2, 1, 600, 'n'), cell(2, 2, 580, 'n'), formula(2, 3, '=B3-C3'),
  cell(3, 0, 'Transport'), cell(3, 1, 300, 'n'), cell(3, 2, 310, 'n'), formula(3, 3, '=B4-C4'),
  cell(4, 0, 'Utilities'), cell(4, 1, 200, 'n'), cell(4, 2, 195, 'n'), formula(4, 3, '=B5-C5'),
  cell(5, 0, 'Entertainment'), cell(5, 1, 250, 'n'), cell(5, 2, 320, 'n'), formula(5, 3, '=B6-C6'),
  cell(6, 0, 'Healthcare'), cell(6, 1, 150, 'n'), cell(6, 2, 80, 'n'), formula(6, 3, '=B7-C7'),
  cell(7, 0, 'Clothing'), cell(7, 1, 200, 'n'), cell(7, 2, 175, 'n'), formula(7, 3, '=B8-C8'),
  cell(8, 0, 'Savings'), cell(8, 1, 500, 'n'), cell(8, 2, 500, 'n'), formula(8, 3, '=B9-C9'),
  cell(9, 0, 'Education'), cell(9, 1, 100, 'n'), cell(9, 2, 150, 'n'), formula(9, 3, '=B10-C10'),
  cell(10, 0, 'Other'), cell(10, 1, 200, 'n'), cell(10, 2, 170, 'n'), formula(10, 3, '=B11-C11'),
  { r: 11, c: 0, v: { v: 'TOTAL', m: 'TOTAL', ct: { fa: 'General', t: 'g' }, bl: 1 } },
  formula(11, 1, '=SUM(B2:B11)'), formula(11, 2, '=SUM(C2:C11)'), formula(11, 3, '=SUM(D2:D11)'),
];

const budgetCellsRu = [
  hdr(0, 0, 'Категория'), hdr(0, 1, 'План'), hdr(0, 2, 'Факт'), hdr(0, 3, 'Разница'),
  cell(1, 0, 'Жильё'), cell(1, 1, 1500, 'n'), cell(1, 2, 1520, 'n'), formula(1, 3, '=B2-C2'),
  cell(2, 0, 'Продукты'), cell(2, 1, 600, 'n'), cell(2, 2, 580, 'n'), formula(2, 3, '=B3-C3'),
  cell(3, 0, 'Транспорт'), cell(3, 1, 300, 'n'), cell(3, 2, 310, 'n'), formula(3, 3, '=B4-C4'),
  cell(4, 0, 'Коммуналка'), cell(4, 1, 200, 'n'), cell(4, 2, 195, 'n'), formula(4, 3, '=B5-C5'),
  cell(5, 0, 'Развлечения'), cell(5, 1, 250, 'n'), cell(5, 2, 320, 'n'), formula(5, 3, '=B6-C6'),
  cell(6, 0, 'Здоровье'), cell(6, 1, 150, 'n'), cell(6, 2, 80, 'n'), formula(6, 3, '=B7-C7'),
  cell(7, 0, 'Одежда'), cell(7, 1, 200, 'n'), cell(7, 2, 175, 'n'), formula(7, 3, '=B8-C8'),
  cell(8, 0, 'Сбережения'), cell(8, 1, 500, 'n'), cell(8, 2, 500, 'n'), formula(8, 3, '=B9-C9'),
  cell(9, 0, 'Образование'), cell(9, 1, 100, 'n'), cell(9, 2, 150, 'n'), formula(9, 3, '=B10-C10'),
  cell(10, 0, 'Прочее'), cell(10, 1, 200, 'n'), cell(10, 2, 170, 'n'), formula(10, 3, '=B11-C11'),
  { r: 11, c: 0, v: { v: 'ИТОГО', m: 'ИТОГО', ct: { fa: 'General', t: 'g' }, bl: 1 } },
  formula(11, 1, '=SUM(B2:B11)'), formula(11, 2, '=SUM(C2:C11)'), formula(11, 3, '=SUM(D2:D11)'),
];

/* ══════════════════════════════════════════════════════
   2. Weekly Schedule
   ══════════════════════════════════════════════════════ */
const scheduleCells = [
  hdr(0, 0, 'Time'), hdr(0, 1, 'Mon'), hdr(0, 2, 'Tue'), hdr(0, 3, 'Wed'), hdr(0, 4, 'Thu'), hdr(0, 5, 'Fri'), hdr(0, 6, 'Sat'), hdr(0, 7, 'Sun'),
  cell(1, 0, '08:00'), cell(1, 1, 'Morning run'), cell(1, 2, 'Morning run'), cell(1, 3, 'Morning run'), cell(1, 4, 'Morning run'), cell(1, 5, 'Morning run'), cell(1, 6, 'Sleep in'), cell(1, 7, 'Sleep in'),
  cell(2, 0, '09:00'), cell(2, 1, 'Team standup'), cell(2, 2, 'Deep work'), cell(2, 3, 'Team standup'), cell(2, 4, 'Deep work'), cell(2, 5, 'Team standup'), cell(2, 6, 'Errands'), cell(2, 7, 'Family time'),
  cell(3, 0, '10:00'), cell(3, 1, 'Deep work'), cell(3, 2, 'Deep work'), cell(3, 3, 'Deep work'), cell(3, 4, 'Deep work'), cell(3, 5, 'Deep work'), cell(3, 6, 'Gym'), cell(3, 7, 'Family time'),
  cell(4, 0, '12:00'), cell(4, 1, 'Lunch'), cell(4, 2, 'Lunch'), cell(4, 3, 'Lunch'), cell(4, 4, 'Lunch'), cell(4, 5, 'Lunch'), cell(4, 6, 'Lunch'), cell(4, 7, 'Lunch'),
  cell(5, 0, '13:00'), cell(5, 1, 'Meetings'), cell(5, 2, 'Code review'), cell(5, 3, 'Meetings'), cell(5, 4, 'Code review'), cell(5, 5, '1:1 meetings'), cell(5, 6, 'Hobbies'), cell(5, 7, 'Rest'),
  cell(6, 0, '15:00'), cell(6, 1, 'Project work'), cell(6, 2, 'Project work'), cell(6, 3, 'Project work'), cell(6, 4, 'Project work'), cell(6, 5, 'Wrap up'), cell(6, 6, 'Hobbies'), cell(6, 7, 'Meal prep'),
  cell(7, 0, '17:00'), cell(7, 1, 'Wrap up'), cell(7, 2, 'Wrap up'), cell(7, 3, 'Wrap up'), cell(7, 4, 'Wrap up'), cell(7, 5, 'Free time'), cell(7, 6, 'Socializing'), cell(7, 7, 'Planning'),
  cell(8, 0, '19:00'), cell(8, 1, 'Dinner'), cell(8, 2, 'Dinner'), cell(8, 3, 'Dinner'), cell(8, 4, 'Dinner'), cell(8, 5, 'Dinner'), cell(8, 6, 'Dinner'), cell(8, 7, 'Dinner'),
  cell(9, 0, '20:00'), cell(9, 1, 'Reading'), cell(9, 2, 'Online course'), cell(9, 3, 'Reading'), cell(9, 4, 'Online course'), cell(9, 5, 'Movie night'), cell(9, 6, 'Free time'), cell(9, 7, 'Free time'),
];

const scheduleCellsRu = [
  hdr(0, 0, 'Время'), hdr(0, 1, 'Пн'), hdr(0, 2, 'Вт'), hdr(0, 3, 'Ср'), hdr(0, 4, 'Чт'), hdr(0, 5, 'Пт'), hdr(0, 6, 'Сб'), hdr(0, 7, 'Вс'),
  cell(1, 0, '08:00'), cell(1, 1, 'Пробежка'), cell(1, 2, 'Пробежка'), cell(1, 3, 'Пробежка'), cell(1, 4, 'Пробежка'), cell(1, 5, 'Пробежка'), cell(1, 6, 'Выспаться'), cell(1, 7, 'Выспаться'),
  cell(2, 0, '09:00'), cell(2, 1, 'Стендап'), cell(2, 2, 'Глубокая работа'), cell(2, 3, 'Стендап'), cell(2, 4, 'Глубокая работа'), cell(2, 5, 'Стендап'), cell(2, 6, 'Дела'), cell(2, 7, 'Семья'),
  cell(3, 0, '10:00'), cell(3, 1, 'Глубокая работа'), cell(3, 2, 'Глубокая работа'), cell(3, 3, 'Глубокая работа'), cell(3, 4, 'Глубокая работа'), cell(3, 5, 'Глубокая работа'), cell(3, 6, 'Спортзал'), cell(3, 7, 'Семья'),
  cell(4, 0, '12:00'), cell(4, 1, 'Обед'), cell(4, 2, 'Обед'), cell(4, 3, 'Обед'), cell(4, 4, 'Обед'), cell(4, 5, 'Обед'), cell(4, 6, 'Обед'), cell(4, 7, 'Обед'),
  cell(5, 0, '13:00'), cell(5, 1, 'Совещания'), cell(5, 2, 'Ревью кода'), cell(5, 3, 'Совещания'), cell(5, 4, 'Ревью кода'), cell(5, 5, 'Встречи 1:1'), cell(5, 6, 'Хобби'), cell(5, 7, 'Отдых'),
  cell(6, 0, '15:00'), cell(6, 1, 'Работа над проектом'), cell(6, 2, 'Работа над проектом'), cell(6, 3, 'Работа над проектом'), cell(6, 4, 'Работа над проектом'), cell(6, 5, 'Завершение дня'), cell(6, 6, 'Хобби'), cell(6, 7, 'Готовка'),
  cell(7, 0, '17:00'), cell(7, 1, 'Завершение дня'), cell(7, 2, 'Завершение дня'), cell(7, 3, 'Завершение дня'), cell(7, 4, 'Завершение дня'), cell(7, 5, 'Свободное время'), cell(7, 6, 'Общение'), cell(7, 7, 'Планирование'),
  cell(8, 0, '19:00'), cell(8, 1, 'Ужин'), cell(8, 2, 'Ужин'), cell(8, 3, 'Ужин'), cell(8, 4, 'Ужин'), cell(8, 5, 'Ужин'), cell(8, 6, 'Ужин'), cell(8, 7, 'Ужин'),
  cell(9, 0, '20:00'), cell(9, 1, 'Чтение'), cell(9, 2, 'Онлайн-курс'), cell(9, 3, 'Чтение'), cell(9, 4, 'Онлайн-курс'), cell(9, 5, 'Кино'), cell(9, 6, 'Свободное время'), cell(9, 7, 'Свободное время'),
];

/* ══════════════════════════════════════════════════════
   3. Gantt Chart
   ══════════════════════════════════════════════════════ */
const ganttCells = [
  hdr(0, 0, 'Task'), hdr(0, 1, 'Start'), hdr(0, 2, 'End'), hdr(0, 3, 'Status'), hdr(0, 4, 'Owner'),
  cell(1, 0, 'Requirements gathering'), cell(1, 1, '2025-01-06'), cell(1, 2, '2025-01-10'), cell(1, 3, 'Done'), cell(1, 4, 'Alice'),
  cell(2, 0, 'UI/UX Design'), cell(2, 1, '2025-01-13'), cell(2, 2, '2025-01-24'), cell(2, 3, 'Done'), cell(2, 4, 'Bob'),
  cell(3, 0, 'Backend development'), cell(3, 1, '2025-01-20'), cell(3, 2, '2025-02-14'), cell(3, 3, 'In Progress'), cell(3, 4, 'Charlie'),
  cell(4, 0, 'Frontend development'), cell(4, 1, '2025-01-27'), cell(4, 2, '2025-02-21'), cell(4, 3, 'In Progress'), cell(4, 4, 'Diana'),
  cell(5, 0, 'API integration'), cell(5, 1, '2025-02-10'), cell(5, 2, '2025-02-21'), cell(5, 3, 'Not Started'), cell(5, 4, 'Charlie'),
  cell(6, 0, 'Testing'), cell(6, 1, '2025-02-17'), cell(6, 2, '2025-02-28'), cell(6, 3, 'Not Started'), cell(6, 4, 'Eve'),
  cell(7, 0, 'Bug fixing'), cell(7, 1, '2025-02-24'), cell(7, 2, '2025-03-07'), cell(7, 3, 'Not Started'), cell(7, 4, 'All'),
  cell(8, 0, 'Documentation'), cell(8, 1, '2025-03-03'), cell(8, 2, '2025-03-07'), cell(8, 3, 'Not Started'), cell(8, 4, 'Alice'),
  cell(9, 0, 'Deployment'), cell(9, 1, '2025-03-10'), cell(9, 2, '2025-03-14'), cell(9, 3, 'Not Started'), cell(9, 4, 'Charlie'),
  cell(10, 0, 'Post-launch review'), cell(10, 1, '2025-03-17'), cell(10, 2, '2025-03-17'), cell(10, 3, 'Not Started'), cell(10, 4, 'All'),
];

const ganttCellsRu = [
  hdr(0, 0, 'Задача'), hdr(0, 1, 'Начало'), hdr(0, 2, 'Конец'), hdr(0, 3, 'Статус'), hdr(0, 4, 'Ответственный'),
  cell(1, 0, 'Сбор требований'), cell(1, 1, '2025-01-06'), cell(1, 2, '2025-01-10'), cell(1, 3, 'Готово'), cell(1, 4, 'Алиса'),
  cell(2, 0, 'Дизайн UI/UX'), cell(2, 1, '2025-01-13'), cell(2, 2, '2025-01-24'), cell(2, 3, 'Готово'), cell(2, 4, 'Борис'),
  cell(3, 0, 'Разработка бэкенда'), cell(3, 1, '2025-01-20'), cell(3, 2, '2025-02-14'), cell(3, 3, 'В работе'), cell(3, 4, 'Сергей'),
  cell(4, 0, 'Разработка фронтенда'), cell(4, 1, '2025-01-27'), cell(4, 2, '2025-02-21'), cell(4, 3, 'В работе'), cell(4, 4, 'Диана'),
  cell(5, 0, 'Интеграция API'), cell(5, 1, '2025-02-10'), cell(5, 2, '2025-02-21'), cell(5, 3, 'Не начато'), cell(5, 4, 'Сергей'),
  cell(6, 0, 'Тестирование'), cell(6, 1, '2025-02-17'), cell(6, 2, '2025-02-28'), cell(6, 3, 'Не начато'), cell(6, 4, 'Елена'),
  cell(7, 0, 'Исправление багов'), cell(7, 1, '2025-02-24'), cell(7, 2, '2025-03-07'), cell(7, 3, 'Не начато'), cell(7, 4, 'Все'),
  cell(8, 0, 'Документация'), cell(8, 1, '2025-03-03'), cell(8, 2, '2025-03-07'), cell(8, 3, 'Не начато'), cell(8, 4, 'Алиса'),
  cell(9, 0, 'Развёртывание'), cell(9, 1, '2025-03-10'), cell(9, 2, '2025-03-14'), cell(9, 3, 'Не начато'), cell(9, 4, 'Сергей'),
  cell(10, 0, 'Ревью после запуска'), cell(10, 1, '2025-03-17'), cell(10, 2, '2025-03-17'), cell(10, 3, 'Не начато'), cell(10, 4, 'Все'),
];

/* ══════════════════════════════════════════════════════
   4. Checklist
   ══════════════════════════════════════════════════════ */
const checklistCells = [
  hdr(0, 0, 'Task'), hdr(0, 1, 'Status'), hdr(0, 2, 'Priority'), hdr(0, 3, 'Deadline'),
  cell(1, 0, 'Finalize project scope'), cell(1, 1, 'Done'), cell(1, 2, 'High'), cell(1, 3, '2025-01-10'),
  cell(2, 0, 'Create wireframes'), cell(2, 1, 'Done'), cell(2, 2, 'High'), cell(2, 3, '2025-01-15'),
  cell(3, 0, 'Set up CI/CD pipeline'), cell(3, 1, 'In Progress'), cell(3, 2, 'Medium'), cell(3, 3, '2025-01-20'),
  cell(4, 0, 'Write unit tests'), cell(4, 1, 'In Progress'), cell(4, 2, 'High'), cell(4, 3, '2025-01-25'),
  cell(5, 0, 'Code review pass'), cell(5, 1, 'Not Started'), cell(5, 2, 'High'), cell(5, 3, '2025-01-30'),
  cell(6, 0, 'Update documentation'), cell(6, 1, 'Not Started'), cell(6, 2, 'Medium'), cell(6, 3, '2025-02-01'),
  cell(7, 0, 'Performance testing'), cell(7, 1, 'Not Started'), cell(7, 2, 'Medium'), cell(7, 3, '2025-02-05'),
  cell(8, 0, 'Security audit'), cell(8, 1, 'Not Started'), cell(8, 2, 'High'), cell(8, 3, '2025-02-10'),
  cell(9, 0, 'Client demo'), cell(9, 1, 'Not Started'), cell(9, 2, 'High'), cell(9, 3, '2025-02-15'),
  cell(10, 0, 'Deploy to production'), cell(10, 1, 'Not Started'), cell(10, 2, 'High'), cell(10, 3, '2025-02-20'),
];

const checklistCellsRu = [
  hdr(0, 0, 'Задача'), hdr(0, 1, 'Статус'), hdr(0, 2, 'Приоритет'), hdr(0, 3, 'Срок'),
  cell(1, 0, 'Утвердить scope проекта'), cell(1, 1, 'Готово'), cell(1, 2, 'Высокий'), cell(1, 3, '2025-01-10'),
  cell(2, 0, 'Создать макеты'), cell(2, 1, 'Готово'), cell(2, 2, 'Высокий'), cell(2, 3, '2025-01-15'),
  cell(3, 0, 'Настроить CI/CD'), cell(3, 1, 'В работе'), cell(3, 2, 'Средний'), cell(3, 3, '2025-01-20'),
  cell(4, 0, 'Написать юнит-тесты'), cell(4, 1, 'В работе'), cell(4, 2, 'Высокий'), cell(4, 3, '2025-01-25'),
  cell(5, 0, 'Пройти ревью кода'), cell(5, 1, 'Не начато'), cell(5, 2, 'Высокий'), cell(5, 3, '2025-01-30'),
  cell(6, 0, 'Обновить документацию'), cell(6, 1, 'Не начато'), cell(6, 2, 'Средний'), cell(6, 3, '2025-02-01'),
  cell(7, 0, 'Тестирование производительности'), cell(7, 1, 'Не начато'), cell(7, 2, 'Средний'), cell(7, 3, '2025-02-05'),
  cell(8, 0, 'Аудит безопасности'), cell(8, 1, 'Не начато'), cell(8, 2, 'Высокий'), cell(8, 3, '2025-02-10'),
  cell(9, 0, 'Демо клиенту'), cell(9, 1, 'Не начато'), cell(9, 2, 'Высокий'), cell(9, 3, '2025-02-15'),
  cell(10, 0, 'Деплой в продакшн'), cell(10, 1, 'Не начато'), cell(10, 2, 'Высокий'), cell(10, 3, '2025-02-20'),
];

/* ══════════════════════════════════════════════════════
   5. Sales Report
   ══════════════════════════════════════════════════════ */
const salesCells = [
  hdr(0, 0, 'Month'), hdr(0, 1, 'Sales'), hdr(0, 2, 'Target'), hdr(0, 3, '% of Target'),
  cell(1, 0, 'January'), cell(1, 1, 42000, 'n'), cell(1, 2, 40000, 'n'), formula(1, 3, '=B2/C2*100'),
  cell(2, 0, 'February'), cell(2, 1, 38500, 'n'), cell(2, 2, 40000, 'n'), formula(2, 3, '=B3/C3*100'),
  cell(3, 0, 'March'), cell(3, 1, 45200, 'n'), cell(3, 2, 45000, 'n'), formula(3, 3, '=B4/C4*100'),
  cell(4, 0, 'April'), cell(4, 1, 41800, 'n'), cell(4, 2, 42000, 'n'), formula(4, 3, '=B5/C5*100'),
  cell(5, 0, 'May'), cell(5, 1, 48900, 'n'), cell(5, 2, 45000, 'n'), formula(5, 3, '=B6/C6*100'),
  cell(6, 0, 'June'), cell(6, 1, 52300, 'n'), cell(6, 2, 50000, 'n'), formula(6, 3, '=B7/C7*100'),
  cell(7, 0, 'July'), cell(7, 1, 47100, 'n'), cell(7, 2, 48000, 'n'), formula(7, 3, '=B8/C8*100'),
  cell(8, 0, 'August'), cell(8, 1, 44600, 'n'), cell(8, 2, 45000, 'n'), formula(8, 3, '=B9/C9*100'),
  cell(9, 0, 'September'), cell(9, 1, 50800, 'n'), cell(9, 2, 50000, 'n'), formula(9, 3, '=B10/C10*100'),
  cell(10, 0, 'October'), cell(10, 1, 53200, 'n'), cell(10, 2, 52000, 'n'), formula(10, 3, '=B11/C11*100'),
  cell(11, 0, 'November'), cell(11, 1, 56100, 'n'), cell(11, 2, 55000, 'n'), formula(11, 3, '=B12/C12*100'),
  cell(12, 0, 'December'), cell(12, 1, 61500, 'n'), cell(12, 2, 60000, 'n'), formula(12, 3, '=B13/C13*100'),
  { r: 13, c: 0, v: { v: 'TOTAL', m: 'TOTAL', ct: { fa: 'General', t: 'g' }, bl: 1 } },
  formula(13, 1, '=SUM(B2:B13)'), formula(13, 2, '=SUM(C2:C13)'), formula(13, 3, '=B14/C14*100'),
];

const salesCellsRu = [
  hdr(0, 0, 'Месяц'), hdr(0, 1, 'Продажи'), hdr(0, 2, 'План'), hdr(0, 3, '% от плана'),
  cell(1, 0, 'Январь'), cell(1, 1, 42000, 'n'), cell(1, 2, 40000, 'n'), formula(1, 3, '=B2/C2*100'),
  cell(2, 0, 'Февраль'), cell(2, 1, 38500, 'n'), cell(2, 2, 40000, 'n'), formula(2, 3, '=B3/C3*100'),
  cell(3, 0, 'Март'), cell(3, 1, 45200, 'n'), cell(3, 2, 45000, 'n'), formula(3, 3, '=B4/C4*100'),
  cell(4, 0, 'Апрель'), cell(4, 1, 41800, 'n'), cell(4, 2, 42000, 'n'), formula(4, 3, '=B5/C5*100'),
  cell(5, 0, 'Май'), cell(5, 1, 48900, 'n'), cell(5, 2, 45000, 'n'), formula(5, 3, '=B6/C6*100'),
  cell(6, 0, 'Июнь'), cell(6, 1, 52300, 'n'), cell(6, 2, 50000, 'n'), formula(6, 3, '=B7/C7*100'),
  cell(7, 0, 'Июль'), cell(7, 1, 47100, 'n'), cell(7, 2, 48000, 'n'), formula(7, 3, '=B8/C8*100'),
  cell(8, 0, 'Август'), cell(8, 1, 44600, 'n'), cell(8, 2, 45000, 'n'), formula(8, 3, '=B9/C9*100'),
  cell(9, 0, 'Сентябрь'), cell(9, 1, 50800, 'n'), cell(9, 2, 50000, 'n'), formula(9, 3, '=B10/C10*100'),
  cell(10, 0, 'Октябрь'), cell(10, 1, 53200, 'n'), cell(10, 2, 52000, 'n'), formula(10, 3, '=B11/C11*100'),
  cell(11, 0, 'Ноябрь'), cell(11, 1, 56100, 'n'), cell(11, 2, 55000, 'n'), formula(11, 3, '=B12/C12*100'),
  cell(12, 0, 'Декабрь'), cell(12, 1, 61500, 'n'), cell(12, 2, 60000, 'n'), formula(12, 3, '=B13/C13*100'),
  { r: 13, c: 0, v: { v: 'ИТОГО', m: 'ИТОГО', ct: { fa: 'General', t: 'g' }, bl: 1 } },
  formula(13, 1, '=SUM(B2:B13)'), formula(13, 2, '=SUM(C2:C13)'), formula(13, 3, '=B14/C14*100'),
];

/* ══════════════════════════════════════════════════════
   6. Payroll
   ══════════════════════════════════════════════════════ */
const payrollCells = [
  hdr(0, 0, 'Employee'), hdr(0, 1, 'Base Salary'), hdr(0, 2, 'Bonus'), hdr(0, 3, 'Deductions'), hdr(0, 4, 'Net Pay'),
  cell(1, 0, 'John Smith'), cell(1, 1, 5000, 'n'), cell(1, 2, 800, 'n'), cell(1, 3, 650, 'n'), formula(1, 4, '=B2+C2-D2'),
  cell(2, 0, 'Jane Doe'), cell(2, 1, 5500, 'n'), cell(2, 2, 600, 'n'), cell(2, 3, 710, 'n'), formula(2, 4, '=B3+C3-D3'),
  cell(3, 0, 'Mike Wilson'), cell(3, 1, 4800, 'n'), cell(3, 2, 1000, 'n'), cell(3, 3, 600, 'n'), formula(3, 4, '=B4+C4-D4'),
  cell(4, 0, 'Sarah Brown'), cell(4, 1, 6200, 'n'), cell(4, 2, 500, 'n'), cell(4, 3, 780, 'n'), formula(4, 4, '=B5+C5-D5'),
  cell(5, 0, 'Tom Davis'), cell(5, 1, 4500, 'n'), cell(5, 2, 700, 'n'), cell(5, 3, 550, 'n'), formula(5, 4, '=B6+C6-D6'),
  cell(6, 0, 'Lisa Garcia'), cell(6, 1, 5800, 'n'), cell(6, 2, 900, 'n'), cell(6, 3, 730, 'n'), formula(6, 4, '=B7+C7-D7'),
  cell(7, 0, 'David Lee'), cell(7, 1, 5100, 'n'), cell(7, 2, 650, 'n'), cell(7, 3, 660, 'n'), formula(7, 4, '=B8+C8-D8'),
  cell(8, 0, 'Emma Martinez'), cell(8, 1, 4900, 'n'), cell(8, 2, 750, 'n'), cell(8, 3, 620, 'n'), formula(8, 4, '=B9+C9-D9'),
  { r: 9, c: 0, v: { v: 'TOTAL', m: 'TOTAL', ct: { fa: 'General', t: 'g' }, bl: 1 } },
  formula(9, 1, '=SUM(B2:B9)'), formula(9, 2, '=SUM(C2:C9)'), formula(9, 3, '=SUM(D2:D9)'), formula(9, 4, '=SUM(E2:E9)'),
];

const payrollCellsRu = [
  hdr(0, 0, 'Сотрудник'), hdr(0, 1, 'Оклад'), hdr(0, 2, 'Премия'), hdr(0, 3, 'Вычеты'), hdr(0, 4, 'К выплате'),
  cell(1, 0, 'Иванов И.И.'), cell(1, 1, 5000, 'n'), cell(1, 2, 800, 'n'), cell(1, 3, 650, 'n'), formula(1, 4, '=B2+C2-D2'),
  cell(2, 0, 'Петрова А.С.'), cell(2, 1, 5500, 'n'), cell(2, 2, 600, 'n'), cell(2, 3, 710, 'n'), formula(2, 4, '=B3+C3-D3'),
  cell(3, 0, 'Сидоров М.В.'), cell(3, 1, 4800, 'n'), cell(3, 2, 1000, 'n'), cell(3, 3, 600, 'n'), formula(3, 4, '=B4+C4-D4'),
  cell(4, 0, 'Козлова С.А.'), cell(4, 1, 6200, 'n'), cell(4, 2, 500, 'n'), cell(4, 3, 780, 'n'), formula(4, 4, '=B5+C5-D5'),
  cell(5, 0, 'Волков Т.Д.'), cell(5, 1, 4500, 'n'), cell(5, 2, 700, 'n'), cell(5, 3, 550, 'n'), formula(5, 4, '=B6+C6-D6'),
  cell(6, 0, 'Морозова Е.К.'), cell(6, 1, 5800, 'n'), cell(6, 2, 900, 'n'), cell(6, 3, 730, 'n'), formula(6, 4, '=B7+C7-D7'),
  cell(7, 0, 'Новиков Д.Л.'), cell(7, 1, 5100, 'n'), cell(7, 2, 650, 'n'), cell(7, 3, 660, 'n'), formula(7, 4, '=B8+C8-D8'),
  cell(8, 0, 'Фёдорова Е.М.'), cell(8, 1, 4900, 'n'), cell(8, 2, 750, 'n'), cell(8, 3, 620, 'n'), formula(8, 4, '=B9+C9-D9'),
  { r: 9, c: 0, v: { v: 'ИТОГО', m: 'ИТОГО', ct: { fa: 'General', t: 'g' }, bl: 1 } },
  formula(9, 1, '=SUM(B2:B9)'), formula(9, 2, '=SUM(C2:C9)'), formula(9, 3, '=SUM(D2:D9)'), formula(9, 4, '=SUM(E2:E9)'),
];

/* ══════════════════════════════════════════════════════
   7. Bookkeeping
   ══════════════════════════════════════════════════════ */
const bookkeepingCells = [
  hdr(0, 0, 'Date'), hdr(0, 1, 'Description'), hdr(0, 2, 'Income'), hdr(0, 3, 'Expense'), hdr(0, 4, 'Balance'),
  cell(1, 0, '2025-01-05'), cell(1, 1, 'Initial balance'), cell(1, 2, 10000, 'n'), cell(1, 3, 0, 'n'), formula(1, 4, '=C2-D2'),
  cell(2, 0, '2025-01-08'), cell(2, 1, 'Office rent'), cell(2, 2, 0, 'n'), cell(2, 3, 1500, 'n'), formula(2, 4, '=E2+C3-D3'),
  cell(3, 0, '2025-01-10'), cell(3, 1, 'Client payment — Project A'), cell(3, 2, 5000, 'n'), cell(3, 3, 0, 'n'), formula(3, 4, '=E3+C4-D4'),
  cell(4, 0, '2025-01-12'), cell(4, 1, 'Software licenses'), cell(4, 2, 0, 'n'), cell(4, 3, 300, 'n'), formula(4, 4, '=E4+C5-D5'),
  cell(5, 0, '2025-01-15'), cell(5, 1, 'Contractor payment'), cell(5, 2, 0, 'n'), cell(5, 3, 2000, 'n'), formula(5, 4, '=E5+C6-D6'),
  cell(6, 0, '2025-01-18'), cell(6, 1, 'Client payment — Project B'), cell(6, 2, 8000, 'n'), cell(6, 3, 0, 'n'), formula(6, 4, '=E6+C7-D7'),
  cell(7, 0, '2025-01-20'), cell(7, 1, 'Office supplies'), cell(7, 2, 0, 'n'), cell(7, 3, 250, 'n'), formula(7, 4, '=E7+C8-D8'),
  cell(8, 0, '2025-01-25'), cell(8, 1, 'Internet & phone'), cell(8, 2, 0, 'n'), cell(8, 3, 120, 'n'), formula(8, 4, '=E8+C9-D9'),
  cell(9, 0, '2025-01-28'), cell(9, 1, 'Client payment — Project C'), cell(9, 2, 3500, 'n'), cell(9, 3, 0, 'n'), formula(9, 4, '=E9+C10-D10'),
  cell(10, 0, '2025-01-31'), cell(10, 1, 'Tax payment'), cell(10, 2, 0, 'n'), cell(10, 3, 1800, 'n'), formula(10, 4, '=E10+C11-D11'),
  { r: 11, c: 0, v: { v: 'TOTAL', m: 'TOTAL', ct: { fa: 'General', t: 'g' }, bl: 1 } },
  formula(11, 2, '=SUM(C2:C11)'), formula(11, 3, '=SUM(D2:D11)'),
];

const bookkeepingCellsRu = [
  hdr(0, 0, 'Дата'), hdr(0, 1, 'Описание'), hdr(0, 2, 'Доход'), hdr(0, 3, 'Расход'), hdr(0, 4, 'Баланс'),
  cell(1, 0, '2025-01-05'), cell(1, 1, 'Начальный баланс'), cell(1, 2, 10000, 'n'), cell(1, 3, 0, 'n'), formula(1, 4, '=C2-D2'),
  cell(2, 0, '2025-01-08'), cell(2, 1, 'Аренда офиса'), cell(2, 2, 0, 'n'), cell(2, 3, 1500, 'n'), formula(2, 4, '=E2+C3-D3'),
  cell(3, 0, '2025-01-10'), cell(3, 1, 'Оплата от клиента — Проект А'), cell(3, 2, 5000, 'n'), cell(3, 3, 0, 'n'), formula(3, 4, '=E3+C4-D4'),
  cell(4, 0, '2025-01-12'), cell(4, 1, 'Лицензии ПО'), cell(4, 2, 0, 'n'), cell(4, 3, 300, 'n'), formula(4, 4, '=E4+C5-D5'),
  cell(5, 0, '2025-01-15'), cell(5, 1, 'Оплата подрядчику'), cell(5, 2, 0, 'n'), cell(5, 3, 2000, 'n'), formula(5, 4, '=E5+C6-D6'),
  cell(6, 0, '2025-01-18'), cell(6, 1, 'Оплата от клиента — Проект Б'), cell(6, 2, 8000, 'n'), cell(6, 3, 0, 'n'), formula(6, 4, '=E6+C7-D7'),
  cell(7, 0, '2025-01-20'), cell(7, 1, 'Канцелярия'), cell(7, 2, 0, 'n'), cell(7, 3, 250, 'n'), formula(7, 4, '=E7+C8-D8'),
  cell(8, 0, '2025-01-25'), cell(8, 1, 'Интернет и связь'), cell(8, 2, 0, 'n'), cell(8, 3, 120, 'n'), formula(8, 4, '=E8+C9-D9'),
  cell(9, 0, '2025-01-28'), cell(9, 1, 'Оплата от клиента — Проект В'), cell(9, 2, 3500, 'n'), cell(9, 3, 0, 'n'), formula(9, 4, '=E9+C10-D10'),
  cell(10, 0, '2025-01-31'), cell(10, 1, 'Уплата налогов'), cell(10, 2, 0, 'n'), cell(10, 3, 1800, 'n'), formula(10, 4, '=E10+C11-D11'),
  { r: 11, c: 0, v: { v: 'ИТОГО', m: 'ИТОГО', ct: { fa: 'General', t: 'g' }, bl: 1 } },
  formula(11, 2, '=SUM(C2:C11)'), formula(11, 3, '=SUM(D2:D11)'),
];

/* ══════════════════════════════════════════════════════
   8. P&L Statement
   ══════════════════════════════════════════════════════ */
const pnlCells = [
  hdr(0, 0, 'Line Item'), hdr(0, 1, 'Q1'), hdr(0, 2, 'Q2'), hdr(0, 3, 'Q3'), hdr(0, 4, 'Q4'), hdr(0, 5, 'Total'),
  { r: 1, c: 0, v: { v: 'Revenue', m: 'Revenue', ct: { fa: 'General', t: 'g' }, bl: 1 } },
  cell(2, 0, 'Product sales'), cell(2, 1, 120000, 'n'), cell(2, 2, 135000, 'n'), cell(2, 3, 128000, 'n'), cell(2, 4, 155000, 'n'), formula(2, 5, '=SUM(B3:E3)'),
  cell(3, 0, 'Service revenue'), cell(3, 1, 45000, 'n'), cell(3, 2, 48000, 'n'), cell(3, 3, 52000, 'n'), cell(3, 4, 50000, 'n'), formula(3, 5, '=SUM(B4:E4)'),
  { r: 4, c: 0, v: { v: 'Total Revenue', m: 'Total Revenue', ct: { fa: 'General', t: 'g' }, bl: 1 } },
  formula(4, 1, '=B3+B4'), formula(4, 2, '=C3+C4'), formula(4, 3, '=D3+D4'), formula(4, 4, '=E3+E4'), formula(4, 5, '=SUM(B5:E5)'),
  { r: 5, c: 0, v: { v: 'Expenses', m: 'Expenses', ct: { fa: 'General', t: 'g' }, bl: 1 } },
  cell(6, 0, 'COGS'), cell(6, 1, 60000, 'n'), cell(6, 2, 65000, 'n'), cell(6, 3, 62000, 'n'), cell(6, 4, 72000, 'n'), formula(6, 5, '=SUM(B7:E7)'),
  cell(7, 0, 'Salaries'), cell(7, 1, 40000, 'n'), cell(7, 2, 40000, 'n'), cell(7, 3, 42000, 'n'), cell(7, 4, 42000, 'n'), formula(7, 5, '=SUM(B8:E8)'),
  cell(8, 0, 'Marketing'), cell(8, 1, 15000, 'n'), cell(8, 2, 18000, 'n'), cell(8, 3, 16000, 'n'), cell(8, 4, 20000, 'n'), formula(8, 5, '=SUM(B9:E9)'),
  cell(9, 0, 'Rent & Utilities'), cell(9, 1, 8000, 'n'), cell(9, 2, 8000, 'n'), cell(9, 3, 8000, 'n'), cell(9, 4, 8000, 'n'), formula(9, 5, '=SUM(B10:E10)'),
  cell(10, 0, 'Other'), cell(10, 1, 5000, 'n'), cell(10, 2, 6000, 'n'), cell(10, 3, 5500, 'n'), cell(10, 4, 7000, 'n'), formula(10, 5, '=SUM(B11:E11)'),
  { r: 11, c: 0, v: { v: 'Total Expenses', m: 'Total Expenses', ct: { fa: 'General', t: 'g' }, bl: 1 } },
  formula(11, 1, '=SUM(B7:B11)'), formula(11, 2, '=SUM(C7:C11)'), formula(11, 3, '=SUM(D7:D11)'), formula(11, 4, '=SUM(E7:E11)'), formula(11, 5, '=SUM(B12:E12)'),
  { r: 12, c: 0, v: { v: 'Net Profit', m: 'Net Profit', ct: { fa: 'General', t: 'g' }, bl: 1 } },
  formula(12, 1, '=B5-B12'), formula(12, 2, '=C5-C12'), formula(12, 3, '=D5-D12'), formula(12, 4, '=E5-E12'), formula(12, 5, '=SUM(B13:E13)'),
];

const pnlCellsRu = [
  hdr(0, 0, 'Статья'), hdr(0, 1, 'Q1'), hdr(0, 2, 'Q2'), hdr(0, 3, 'Q3'), hdr(0, 4, 'Q4'), hdr(0, 5, 'Итого'),
  { r: 1, c: 0, v: { v: 'Выручка', m: 'Выручка', ct: { fa: 'General', t: 'g' }, bl: 1 } },
  cell(2, 0, 'Продажи продукции'), cell(2, 1, 120000, 'n'), cell(2, 2, 135000, 'n'), cell(2, 3, 128000, 'n'), cell(2, 4, 155000, 'n'), formula(2, 5, '=SUM(B3:E3)'),
  cell(3, 0, 'Доход от услуг'), cell(3, 1, 45000, 'n'), cell(3, 2, 48000, 'n'), cell(3, 3, 52000, 'n'), cell(3, 4, 50000, 'n'), formula(3, 5, '=SUM(B4:E4)'),
  { r: 4, c: 0, v: { v: 'Итого выручка', m: 'Итого выручка', ct: { fa: 'General', t: 'g' }, bl: 1 } },
  formula(4, 1, '=B3+B4'), formula(4, 2, '=C3+C4'), formula(4, 3, '=D3+D4'), formula(4, 4, '=E3+E4'), formula(4, 5, '=SUM(B5:E5)'),
  { r: 5, c: 0, v: { v: 'Расходы', m: 'Расходы', ct: { fa: 'General', t: 'g' }, bl: 1 } },
  cell(6, 0, 'Себестоимость'), cell(6, 1, 60000, 'n'), cell(6, 2, 65000, 'n'), cell(6, 3, 62000, 'n'), cell(6, 4, 72000, 'n'), formula(6, 5, '=SUM(B7:E7)'),
  cell(7, 0, 'Зарплаты'), cell(7, 1, 40000, 'n'), cell(7, 2, 40000, 'n'), cell(7, 3, 42000, 'n'), cell(7, 4, 42000, 'n'), formula(7, 5, '=SUM(B8:E8)'),
  cell(8, 0, 'Маркетинг'), cell(8, 1, 15000, 'n'), cell(8, 2, 18000, 'n'), cell(8, 3, 16000, 'n'), cell(8, 4, 20000, 'n'), formula(8, 5, '=SUM(B9:E9)'),
  cell(9, 0, 'Аренда и коммуналка'), cell(9, 1, 8000, 'n'), cell(9, 2, 8000, 'n'), cell(9, 3, 8000, 'n'), cell(9, 4, 8000, 'n'), formula(9, 5, '=SUM(B10:E10)'),
  cell(10, 0, 'Прочее'), cell(10, 1, 5000, 'n'), cell(10, 2, 6000, 'n'), cell(10, 3, 5500, 'n'), cell(10, 4, 7000, 'n'), formula(10, 5, '=SUM(B11:E11)'),
  { r: 11, c: 0, v: { v: 'Итого расходы', m: 'Итого расходы', ct: { fa: 'General', t: 'g' }, bl: 1 } },
  formula(11, 1, '=SUM(B7:B11)'), formula(11, 2, '=SUM(C7:C11)'), formula(11, 3, '=SUM(D7:D11)'), formula(11, 4, '=SUM(E7:E11)'), formula(11, 5, '=SUM(B12:E12)'),
  { r: 12, c: 0, v: { v: 'Чистая прибыль', m: 'Чистая прибыль', ct: { fa: 'General', t: 'g' }, bl: 1 } },
  formula(12, 1, '=B5-B12'), formula(12, 2, '=C5-C12'), formula(12, 3, '=D5-D12'), formula(12, 4, '=E5-E12'), formula(12, 5, '=SUM(B13:E13)'),
];

/* ══════════════════════════════════════════════════════
   9. Timesheet
   ══════════════════════════════════════════════════════ */
const timesheetHeaders = [
  hdr(0, 0, 'Employee'),
  ...Array.from({ length: 7 }, (_, i) => hdr(0, i + 1, `Day ${i + 1}`)),
  hdr(0, 8, 'Total'),
];
const timesheetRows = [
  ['John Smith', 8, 8, 8, 8, 8, 0, 0],
  ['Jane Doe', 8, 8, 8, 8, 6, 4, 0],
  ['Mike Wilson', 8, 7, 8, 8, 8, 0, 0],
  ['Sarah Brown', 8, 8, 8, 8, 8, 0, 0],
  ['Tom Davis', 6, 8, 8, 8, 8, 4, 0],
  ['Lisa Garcia', 8, 8, 8, 8, 8, 0, 0],
  ['David Lee', 8, 8, 8, 0, 8, 8, 0],
  ['Emma Martinez', 8, 8, 8, 8, 8, 0, 0],
];
const timesheetCells = [
  ...timesheetHeaders,
  ...timesheetRows.flatMap((row, ri) => [
    cell(ri + 1, 0, row[0] as string),
    ...row.slice(1).map((v, ci) => cell(ri + 1, ci + 1, v as number, 'n')),
    formula(ri + 1, 8, `=SUM(B${ri + 2}:H${ri + 2})`),
  ]),
  { r: 9, c: 0, v: { v: 'AVERAGE', m: 'AVERAGE', ct: { fa: 'General', t: 'g' }, bl: 1 } },
  formula(9, 8, '=AVERAGE(I2:I9)'),
];

const timesheetHeadersRu = [
  hdr(0, 0, 'Сотрудник'),
  ...Array.from({ length: 7 }, (_, i) => hdr(0, i + 1, `День ${i + 1}`)),
  hdr(0, 8, 'Итого'),
];
const timesheetRowsRu = [
  ['Иванов И.И.', 8, 8, 8, 8, 8, 0, 0],
  ['Петрова А.С.', 8, 8, 8, 8, 6, 4, 0],
  ['Сидоров М.В.', 8, 7, 8, 8, 8, 0, 0],
  ['Козлова С.А.', 8, 8, 8, 8, 8, 0, 0],
  ['Волков Т.Д.', 6, 8, 8, 8, 8, 4, 0],
  ['Морозова Е.К.', 8, 8, 8, 8, 8, 0, 0],
  ['Новиков Д.Л.', 8, 8, 8, 0, 8, 8, 0],
  ['Фёдорова Е.М.', 8, 8, 8, 8, 8, 0, 0],
];
const timesheetCellsRu = [
  ...timesheetHeadersRu,
  ...timesheetRowsRu.flatMap((row, ri) => [
    cell(ri + 1, 0, row[0] as string),
    ...row.slice(1).map((v, ci) => cell(ri + 1, ci + 1, v as number, 'n')),
    formula(ri + 1, 8, `=SUM(B${ri + 2}:H${ri + 2})`),
  ]),
  { r: 9, c: 0, v: { v: 'СРЕДНЕЕ', m: 'СРЕДНЕЕ', ct: { fa: 'General', t: 'g' }, bl: 1 } },
  formula(9, 8, '=AVERAGE(I2:I9)'),
];

/* ══════════════════════════════════════════════════════
   10. KPI
   ══════════════════════════════════════════════════════ */
const kpiCells = [
  hdr(0, 0, 'Employee'), hdr(0, 1, 'KPI'), hdr(0, 2, 'Target'), hdr(0, 3, 'Actual'), hdr(0, 4, '% Achieved'),
  cell(1, 0, 'Alice'), cell(1, 1, 'Revenue ($K)'), cell(1, 2, 100, 'n'), cell(1, 3, 112, 'n'), formula(1, 4, '=D2/C2*100'),
  cell(2, 0, 'Bob'), cell(2, 1, 'New clients'), cell(2, 2, 20, 'n'), cell(2, 3, 18, 'n'), formula(2, 4, '=D3/C3*100'),
  cell(3, 0, 'Charlie'), cell(3, 1, 'Tickets closed'), cell(3, 2, 150, 'n'), cell(3, 3, 165, 'n'), formula(3, 4, '=D4/C4*100'),
  cell(4, 0, 'Diana'), cell(4, 1, 'Conversion rate %'), cell(4, 2, 5, 'n'), cell(4, 3, 4.8, 'n'), formula(4, 4, '=D5/C5*100'),
  cell(5, 0, 'Eve'), cell(5, 1, 'CSAT score'), cell(5, 2, 90, 'n'), cell(5, 3, 92, 'n'), formula(5, 4, '=D6/C6*100'),
  cell(6, 0, 'Frank'), cell(6, 1, 'Revenue ($K)'), cell(6, 2, 80, 'n'), cell(6, 3, 75, 'n'), formula(6, 4, '=D7/C7*100'),
  cell(7, 0, 'Grace'), cell(7, 1, 'New clients'), cell(7, 2, 15, 'n'), cell(7, 3, 19, 'n'), formula(7, 4, '=D8/C8*100'),
  cell(8, 0, 'Hank'), cell(8, 1, 'Tickets closed'), cell(8, 2, 120, 'n'), cell(8, 3, 110, 'n'), formula(8, 4, '=D9/C9*100'),
  { r: 9, c: 0, v: { v: 'AVERAGE', m: 'AVERAGE', ct: { fa: 'General', t: 'g' }, bl: 1 } },
  formula(9, 4, '=AVERAGE(E2:E9)'),
];

const kpiCellsRu = [
  hdr(0, 0, 'Сотрудник'), hdr(0, 1, 'KPI'), hdr(0, 2, 'Цель'), hdr(0, 3, 'Факт'), hdr(0, 4, '% выполнения'),
  cell(1, 0, 'Алиса'), cell(1, 1, 'Выручка (тыс.)'), cell(1, 2, 100, 'n'), cell(1, 3, 112, 'n'), formula(1, 4, '=D2/C2*100'),
  cell(2, 0, 'Борис'), cell(2, 1, 'Новые клиенты'), cell(2, 2, 20, 'n'), cell(2, 3, 18, 'n'), formula(2, 4, '=D3/C3*100'),
  cell(3, 0, 'Сергей'), cell(3, 1, 'Закрытые тикеты'), cell(3, 2, 150, 'n'), cell(3, 3, 165, 'n'), formula(3, 4, '=D4/C4*100'),
  cell(4, 0, 'Диана'), cell(4, 1, 'Конверсия %'), cell(4, 2, 5, 'n'), cell(4, 3, 4.8, 'n'), formula(4, 4, '=D5/C5*100'),
  cell(5, 0, 'Елена'), cell(5, 1, 'CSAT оценка'), cell(5, 2, 90, 'n'), cell(5, 3, 92, 'n'), formula(5, 4, '=D6/C6*100'),
  cell(6, 0, 'Фёдор'), cell(6, 1, 'Выручка (тыс.)'), cell(6, 2, 80, 'n'), cell(6, 3, 75, 'n'), formula(6, 4, '=D7/C7*100'),
  cell(7, 0, 'Мария'), cell(7, 1, 'Новые клиенты'), cell(7, 2, 15, 'n'), cell(7, 3, 19, 'n'), formula(7, 4, '=D8/C8*100'),
  cell(8, 0, 'Николай'), cell(8, 1, 'Закрытые тикеты'), cell(8, 2, 120, 'n'), cell(8, 3, 110, 'n'), formula(8, 4, '=D9/C9*100'),
  { r: 9, c: 0, v: { v: 'СРЕДНЕЕ', m: 'СРЕДНЕЕ', ct: { fa: 'General', t: 'g' }, bl: 1 } },
  formula(9, 4, '=AVERAGE(E2:E9)'),
];

/* ══════════════════════════════════════════════════════
   11. Inventory
   ══════════════════════════════════════════════════════ */
const inventoryCells = [
  hdr(0, 0, 'SKU'), hdr(0, 1, 'Product'), hdr(0, 2, 'Qty'), hdr(0, 3, 'Unit Price'), hdr(0, 4, 'Total Value'),
  cell(1, 0, 'SKU-001'), cell(1, 1, 'Wireless Mouse'), cell(1, 2, 150, 'n'), cell(1, 3, 25.99, 'n'), formula(1, 4, '=C2*D2'),
  cell(2, 0, 'SKU-002'), cell(2, 1, 'Mechanical Keyboard'), cell(2, 2, 80, 'n'), cell(2, 3, 79.99, 'n'), formula(2, 4, '=C3*D3'),
  cell(3, 0, 'SKU-003'), cell(3, 1, 'USB-C Hub'), cell(3, 2, 200, 'n'), cell(3, 3, 34.99, 'n'), formula(3, 4, '=C4*D4'),
  cell(4, 0, 'SKU-004'), cell(4, 1, 'Monitor Stand'), cell(4, 2, 60, 'n'), cell(4, 3, 45.00, 'n'), formula(4, 4, '=C5*D5'),
  cell(5, 0, 'SKU-005'), cell(5, 1, 'Webcam HD'), cell(5, 2, 120, 'n'), cell(5, 3, 59.99, 'n'), formula(5, 4, '=C6*D6'),
  cell(6, 0, 'SKU-006'), cell(6, 1, 'Desk Lamp'), cell(6, 2, 90, 'n'), cell(6, 3, 32.50, 'n'), formula(6, 4, '=C7*D7'),
  cell(7, 0, 'SKU-007'), cell(7, 1, 'Headset'), cell(7, 2, 110, 'n'), cell(7, 3, 89.99, 'n'), formula(7, 4, '=C8*D8'),
  cell(8, 0, 'SKU-008'), cell(8, 1, 'Mouse Pad XL'), cell(8, 2, 300, 'n'), cell(8, 3, 14.99, 'n'), formula(8, 4, '=C9*D9'),
  cell(9, 0, 'SKU-009'), cell(9, 1, 'Cable Organizer'), cell(9, 2, 250, 'n'), cell(9, 3, 9.99, 'n'), formula(9, 4, '=C10*D10'),
  cell(10, 0, 'SKU-010'), cell(10, 1, 'Laptop Stand'), cell(10, 2, 75, 'n'), cell(10, 3, 55.00, 'n'), formula(10, 4, '=C11*D11'),
  { r: 11, c: 0, v: { v: '', m: '', ct: { fa: 'General', t: 'g' } } },
  { r: 11, c: 1, v: { v: 'TOTAL', m: 'TOTAL', ct: { fa: 'General', t: 'g' }, bl: 1 } },
  formula(11, 2, '=SUM(C2:C11)'), formula(11, 4, '=SUM(E2:E11)'),
];

const inventoryCellsRu = [
  hdr(0, 0, 'Артикул'), hdr(0, 1, 'Название'), hdr(0, 2, 'Кол-во'), hdr(0, 3, 'Цена за ед.'), hdr(0, 4, 'Сумма'),
  cell(1, 0, 'АРТ-001'), cell(1, 1, 'Беспроводная мышь'), cell(1, 2, 150, 'n'), cell(1, 3, 25.99, 'n'), formula(1, 4, '=C2*D2'),
  cell(2, 0, 'АРТ-002'), cell(2, 1, 'Механическая клавиатура'), cell(2, 2, 80, 'n'), cell(2, 3, 79.99, 'n'), formula(2, 4, '=C3*D3'),
  cell(3, 0, 'АРТ-003'), cell(3, 1, 'USB-C хаб'), cell(3, 2, 200, 'n'), cell(3, 3, 34.99, 'n'), formula(3, 4, '=C4*D4'),
  cell(4, 0, 'АРТ-004'), cell(4, 1, 'Подставка для монитора'), cell(4, 2, 60, 'n'), cell(4, 3, 45.00, 'n'), formula(4, 4, '=C5*D5'),
  cell(5, 0, 'АРТ-005'), cell(5, 1, 'Веб-камера HD'), cell(5, 2, 120, 'n'), cell(5, 3, 59.99, 'n'), formula(5, 4, '=C6*D6'),
  cell(6, 0, 'АРТ-006'), cell(6, 1, 'Настольная лампа'), cell(6, 2, 90, 'n'), cell(6, 3, 32.50, 'n'), formula(6, 4, '=C7*D7'),
  cell(7, 0, 'АРТ-007'), cell(7, 1, 'Гарнитура'), cell(7, 2, 110, 'n'), cell(7, 3, 89.99, 'n'), formula(7, 4, '=C8*D8'),
  cell(8, 0, 'АРТ-008'), cell(8, 1, 'Коврик для мыши XL'), cell(8, 2, 300, 'n'), cell(8, 3, 14.99, 'n'), formula(8, 4, '=C9*D9'),
  cell(9, 0, 'АРТ-009'), cell(9, 1, 'Органайзер для кабелей'), cell(9, 2, 250, 'n'), cell(9, 3, 9.99, 'n'), formula(9, 4, '=C10*D10'),
  cell(10, 0, 'АРТ-010'), cell(10, 1, 'Подставка для ноутбука'), cell(10, 2, 75, 'n'), cell(10, 3, 55.00, 'n'), formula(10, 4, '=C11*D11'),
  { r: 11, c: 0, v: { v: '', m: '', ct: { fa: 'General', t: 'g' } } },
  { r: 11, c: 1, v: { v: 'ИТОГО', m: 'ИТОГО', ct: { fa: 'General', t: 'g' }, bl: 1 } },
  formula(11, 2, '=SUM(C2:C11)'), formula(11, 4, '=SUM(E2:E11)'),
];

/* ══════════════════════════════════════════════════════
   12. Client List
   ══════════════════════════════════════════════════════ */
const clientCells = [
  hdr(0, 0, 'Company'), hdr(0, 1, 'Contact'), hdr(0, 2, 'Phone'), hdr(0, 3, 'Email'), hdr(0, 4, 'Status'),
  cell(1, 0, 'Acme Corp'), cell(1, 1, 'John Adams'), cell(1, 2, '+1-555-0101'), cell(1, 3, 'john@acme.com'), cell(1, 4, 'Active'),
  cell(2, 0, 'Globex Inc'), cell(2, 1, 'Lisa Park'), cell(2, 2, '+1-555-0102'), cell(2, 3, 'lisa@globex.com'), cell(2, 4, 'Active'),
  cell(3, 0, 'Initech'), cell(3, 1, 'Peter Gibbons'), cell(3, 2, '+1-555-0103'), cell(3, 3, 'peter@initech.com'), cell(3, 4, 'Lead'),
  cell(4, 0, 'Umbrella Ltd'), cell(4, 1, 'Alice Wong'), cell(4, 2, '+1-555-0104'), cell(4, 3, 'alice@umbrella.com'), cell(4, 4, 'Active'),
  cell(5, 0, 'Stark Industries'), cell(5, 1, 'Tony Rivera'), cell(5, 2, '+1-555-0105'), cell(5, 3, 'tony@stark.com'), cell(5, 4, 'Churned'),
  cell(6, 0, 'Wayne Enterprises'), cell(6, 1, 'Bruce Kent'), cell(6, 2, '+1-555-0106'), cell(6, 3, 'bruce@wayne.com'), cell(6, 4, 'Active'),
  cell(7, 0, 'Cyberdyne Systems'), cell(7, 1, 'Sarah Connor'), cell(7, 2, '+1-555-0107'), cell(7, 3, 'sarah@cyberdyne.com'), cell(7, 4, 'Lead'),
  cell(8, 0, 'Wonka Industries'), cell(8, 1, 'Charlie Bucket'), cell(8, 2, '+1-555-0108'), cell(8, 3, 'charlie@wonka.com'), cell(8, 4, 'Active'),
  cell(9, 0, 'Dunder Mifflin'), cell(9, 1, 'Michael Scott'), cell(9, 2, '+1-555-0109'), cell(9, 3, 'michael@dm.com'), cell(9, 4, 'Lead'),
  cell(10, 0, 'Pied Piper'), cell(10, 1, 'Richard Hendricks'), cell(10, 2, '+1-555-0110'), cell(10, 3, 'richard@pp.com'), cell(10, 4, 'Active'),
];

const clientCellsRu = [
  hdr(0, 0, 'Компания'), hdr(0, 1, 'Контакт'), hdr(0, 2, 'Телефон'), hdr(0, 3, 'Email'), hdr(0, 4, 'Статус'),
  cell(1, 0, 'Альфа Групп'), cell(1, 1, 'Иванов А.А.'), cell(1, 2, '+7-495-001-01-01'), cell(1, 3, 'ivanov@alfa.ru'), cell(1, 4, 'Активный'),
  cell(2, 0, 'Бета Сервис'), cell(2, 1, 'Петрова Л.С.'), cell(2, 2, '+7-495-002-02-02'), cell(2, 3, 'petrova@beta.ru'), cell(2, 4, 'Активный'),
  cell(3, 0, 'Гамма Тех'), cell(3, 1, 'Сидоров П.И.'), cell(3, 2, '+7-495-003-03-03'), cell(3, 3, 'sidorov@gamma.ru'), cell(3, 4, 'Лид'),
  cell(4, 0, 'Дельта Софт'), cell(4, 1, 'Козлова А.В.'), cell(4, 2, '+7-495-004-04-04'), cell(4, 3, 'kozlova@delta.ru'), cell(4, 4, 'Активный'),
  cell(5, 0, 'Эпсилон Трейд'), cell(5, 1, 'Волков Т.Р.'), cell(5, 2, '+7-495-005-05-05'), cell(5, 3, 'volkov@epsilon.ru'), cell(5, 4, 'Неактивный'),
  cell(6, 0, 'Зета Консалт'), cell(6, 1, 'Морозов Б.К.'), cell(6, 2, '+7-495-006-06-06'), cell(6, 3, 'morozov@zeta.ru'), cell(6, 4, 'Активный'),
  cell(7, 0, 'Тета Системс'), cell(7, 1, 'Новикова С.Е.'), cell(7, 2, '+7-495-007-07-07'), cell(7, 3, 'novikova@theta.ru'), cell(7, 4, 'Лид'),
  cell(8, 0, 'Йота Индастриз'), cell(8, 1, 'Фёдоров К.Д.'), cell(8, 2, '+7-495-008-08-08'), cell(8, 3, 'fedorov@iota.ru'), cell(8, 4, 'Активный'),
  cell(9, 0, 'Каппа Медиа'), cell(9, 1, 'Смирнов М.А.'), cell(9, 2, '+7-495-009-09-09'), cell(9, 3, 'smirnov@kappa.ru'), cell(9, 4, 'Лид'),
  cell(10, 0, 'Лямбда Финанс'), cell(10, 1, 'Кузнецов Р.О.'), cell(10, 2, '+7-495-010-10-10'), cell(10, 3, 'kuznecov@lambda.ru'), cell(10, 4, 'Активный'),
];

/* ══════════════════════════════════════════════════════
   13. Project Tracker
   ══════════════════════════════════════════════════════ */
const projectCells = [
  hdr(0, 0, 'Project'), hdr(0, 1, 'Status'), hdr(0, 2, 'Budget'), hdr(0, 3, 'Spent'), hdr(0, 4, 'Deadline'),
  cell(1, 0, 'Website Redesign'), cell(1, 1, 'In Progress'), cell(1, 2, 25000, 'n'), cell(1, 3, 18500, 'n'), cell(1, 4, '2025-03-15'),
  cell(2, 0, 'Mobile App v2'), cell(2, 1, 'In Progress'), cell(2, 2, 60000, 'n'), cell(2, 3, 42000, 'n'), cell(2, 4, '2025-04-30'),
  cell(3, 0, 'CRM Integration'), cell(3, 1, 'Planning'), cell(3, 2, 15000, 'n'), cell(3, 3, 2000, 'n'), cell(3, 4, '2025-06-01'),
  cell(4, 0, 'Data Migration'), cell(4, 1, 'Done'), cell(4, 2, 10000, 'n'), cell(4, 3, 9800, 'n'), cell(4, 4, '2025-01-31'),
  cell(5, 0, 'Security Audit'), cell(5, 1, 'In Progress'), cell(5, 2, 8000, 'n'), cell(5, 3, 5500, 'n'), cell(5, 4, '2025-02-28'),
  cell(6, 0, 'Analytics Dashboard'), cell(6, 1, 'Planning'), cell(6, 2, 20000, 'n'), cell(6, 3, 0, 'n'), cell(6, 4, '2025-07-15'),
  cell(7, 0, 'API Gateway'), cell(7, 1, 'Done'), cell(7, 2, 12000, 'n'), cell(7, 3, 11200, 'n'), cell(7, 4, '2025-01-15'),
  cell(8, 0, 'Email Campaign Tool'), cell(8, 1, 'On Hold'), cell(8, 2, 18000, 'n'), cell(8, 3, 7000, 'n'), cell(8, 4, '2025-05-30'),
  { r: 9, c: 0, v: { v: 'TOTAL', m: 'TOTAL', ct: { fa: 'General', t: 'g' }, bl: 1 } },
  formula(9, 2, '=SUM(C2:C9)'), formula(9, 3, '=SUM(D2:D9)'),
];

const projectCellsRu = [
  hdr(0, 0, 'Проект'), hdr(0, 1, 'Статус'), hdr(0, 2, 'Бюджет'), hdr(0, 3, 'Потрачено'), hdr(0, 4, 'Дедлайн'),
  cell(1, 0, 'Редизайн сайта'), cell(1, 1, 'В работе'), cell(1, 2, 25000, 'n'), cell(1, 3, 18500, 'n'), cell(1, 4, '2025-03-15'),
  cell(2, 0, 'Мобильное приложение v2'), cell(2, 1, 'В работе'), cell(2, 2, 60000, 'n'), cell(2, 3, 42000, 'n'), cell(2, 4, '2025-04-30'),
  cell(3, 0, 'Интеграция CRM'), cell(3, 1, 'Планирование'), cell(3, 2, 15000, 'n'), cell(3, 3, 2000, 'n'), cell(3, 4, '2025-06-01'),
  cell(4, 0, 'Миграция данных'), cell(4, 1, 'Готово'), cell(4, 2, 10000, 'n'), cell(4, 3, 9800, 'n'), cell(4, 4, '2025-01-31'),
  cell(5, 0, 'Аудит безопасности'), cell(5, 1, 'В работе'), cell(5, 2, 8000, 'n'), cell(5, 3, 5500, 'n'), cell(5, 4, '2025-02-28'),
  cell(6, 0, 'Дашборд аналитики'), cell(6, 1, 'Планирование'), cell(6, 2, 20000, 'n'), cell(6, 3, 0, 'n'), cell(6, 4, '2025-07-15'),
  cell(7, 0, 'API-шлюз'), cell(7, 1, 'Готово'), cell(7, 2, 12000, 'n'), cell(7, 3, 11200, 'n'), cell(7, 4, '2025-01-15'),
  cell(8, 0, 'Инструмент email-рассылок'), cell(8, 1, 'Приостановлен'), cell(8, 2, 18000, 'n'), cell(8, 3, 7000, 'n'), cell(8, 4, '2025-05-30'),
  { r: 9, c: 0, v: { v: 'ИТОГО', m: 'ИТОГО', ct: { fa: 'General', t: 'g' }, bl: 1 } },
  formula(9, 2, '=SUM(C2:C9)'), formula(9, 3, '=SUM(D2:D9)'),
];

/* ══════════════════════════════════════════════════════
   14. Expense Report
   ══════════════════════════════════════════════════════ */
const expenseCells = [
  hdr(0, 0, 'Date'), hdr(0, 1, 'Description'), hdr(0, 2, 'Category'), hdr(0, 3, 'Amount'), hdr(0, 4, 'Receipt'),
  cell(1, 0, '2025-01-06'), cell(1, 1, 'Flight to NYC'), cell(1, 2, 'Travel'), cell(1, 3, 450, 'n'), cell(1, 4, 'Yes'),
  cell(2, 0, '2025-01-06'), cell(2, 1, 'Hotel — 3 nights'), cell(2, 2, 'Accommodation'), cell(2, 3, 680, 'n'), cell(2, 4, 'Yes'),
  cell(3, 0, '2025-01-07'), cell(3, 1, 'Client dinner'), cell(3, 2, 'Meals'), cell(3, 3, 125, 'n'), cell(3, 4, 'Yes'),
  cell(4, 0, '2025-01-07'), cell(4, 1, 'Taxi to meeting'), cell(4, 2, 'Transport'), cell(4, 3, 35, 'n'), cell(4, 4, 'Yes'),
  cell(5, 0, '2025-01-08'), cell(5, 1, 'Conference ticket'), cell(5, 2, 'Events'), cell(5, 3, 299, 'n'), cell(5, 4, 'Yes'),
  cell(6, 0, '2025-01-08'), cell(6, 1, 'Lunch with team'), cell(6, 2, 'Meals'), cell(6, 3, 85, 'n'), cell(6, 4, 'Yes'),
  cell(7, 0, '2025-01-08'), cell(7, 1, 'Office supplies'), cell(7, 2, 'Supplies'), cell(7, 3, 42, 'n'), cell(7, 4, 'No'),
  cell(8, 0, '2025-01-09'), cell(8, 1, 'Return flight'), cell(8, 2, 'Travel'), cell(8, 3, 380, 'n'), cell(8, 4, 'Yes'),
  cell(9, 0, '2025-01-09'), cell(9, 1, 'Airport parking'), cell(9, 2, 'Transport'), cell(9, 3, 60, 'n'), cell(9, 4, 'Yes'),
  cell(10, 0, '2025-01-10'), cell(10, 1, 'Printed materials'), cell(10, 2, 'Supplies'), cell(10, 3, 28, 'n'), cell(10, 4, 'No'),
  { r: 11, c: 0, v: { v: '', m: '', ct: { fa: 'General', t: 'g' } } },
  { r: 11, c: 1, v: { v: 'TOTAL', m: 'TOTAL', ct: { fa: 'General', t: 'g' }, bl: 1 } },
  formula(11, 3, '=SUM(D2:D11)'),
];

const expenseCellsRu = [
  hdr(0, 0, 'Дата'), hdr(0, 1, 'Описание'), hdr(0, 2, 'Категория'), hdr(0, 3, 'Сумма'), hdr(0, 4, 'Чек'),
  cell(1, 0, '2025-01-06'), cell(1, 1, 'Перелёт в Москву'), cell(1, 2, 'Командировка'), cell(1, 3, 450, 'n'), cell(1, 4, 'Да'),
  cell(2, 0, '2025-01-06'), cell(2, 1, 'Гостиница — 3 ночи'), cell(2, 2, 'Проживание'), cell(2, 3, 680, 'n'), cell(2, 4, 'Да'),
  cell(3, 0, '2025-01-07'), cell(3, 1, 'Ужин с клиентом'), cell(3, 2, 'Питание'), cell(3, 3, 125, 'n'), cell(3, 4, 'Да'),
  cell(4, 0, '2025-01-07'), cell(4, 1, 'Такси на встречу'), cell(4, 2, 'Транспорт'), cell(4, 3, 35, 'n'), cell(4, 4, 'Да'),
  cell(5, 0, '2025-01-08'), cell(5, 1, 'Билет на конференцию'), cell(5, 2, 'Мероприятия'), cell(5, 3, 299, 'n'), cell(5, 4, 'Да'),
  cell(6, 0, '2025-01-08'), cell(6, 1, 'Обед с командой'), cell(6, 2, 'Питание'), cell(6, 3, 85, 'n'), cell(6, 4, 'Да'),
  cell(7, 0, '2025-01-08'), cell(7, 1, 'Канцелярия'), cell(7, 2, 'Расходные материалы'), cell(7, 3, 42, 'n'), cell(7, 4, 'Нет'),
  cell(8, 0, '2025-01-09'), cell(8, 1, 'Обратный перелёт'), cell(8, 2, 'Командировка'), cell(8, 3, 380, 'n'), cell(8, 4, 'Да'),
  cell(9, 0, '2025-01-09'), cell(9, 1, 'Парковка в аэропорту'), cell(9, 2, 'Транспорт'), cell(9, 3, 60, 'n'), cell(9, 4, 'Да'),
  cell(10, 0, '2025-01-10'), cell(10, 1, 'Печатные материалы'), cell(10, 2, 'Расходные материалы'), cell(10, 3, 28, 'n'), cell(10, 4, 'Нет'),
  { r: 11, c: 0, v: { v: '', m: '', ct: { fa: 'General', t: 'g' } } },
  { r: 11, c: 1, v: { v: 'ИТОГО', m: 'ИТОГО', ct: { fa: 'General', t: 'g' }, bl: 1 } },
  formula(11, 3, '=SUM(D2:D11)'),
];

/* ══════════════════════════════════════════════════════
   15. Meeting Notes
   ══════════════════════════════════════════════════════ */
const meetingCells = [
  hdr(0, 0, 'Date'), hdr(0, 1, 'Topic'), hdr(0, 2, 'Attendees'), hdr(0, 3, 'Decisions'), hdr(0, 4, 'Due Date'),
  cell(1, 0, '2025-01-06'), cell(1, 1, 'Q1 planning'), cell(1, 2, 'Alice, Bob, Charlie'), cell(1, 3, 'Set Q1 OKRs; hire 2 engineers'), cell(1, 4, '2025-01-20'),
  cell(2, 0, '2025-01-08'), cell(2, 1, 'Product roadmap'), cell(2, 2, 'Diana, Eve, Frank'), cell(2, 3, 'Prioritize mobile app v2'), cell(2, 4, '2025-01-15'),
  cell(3, 0, '2025-01-13'), cell(3, 1, 'Sprint review'), cell(3, 2, 'Full team'), cell(3, 3, 'Deploy auth module; fix 12 bugs'), cell(3, 4, '2025-01-17'),
  cell(4, 0, '2025-01-15'), cell(4, 1, 'Client feedback'), cell(4, 2, 'Alice, Grace'), cell(4, 3, 'Add export to PDF; improve UX'), cell(4, 4, '2025-02-01'),
  cell(5, 0, '2025-01-20'), cell(5, 1, 'Budget review'), cell(5, 2, 'Bob, Hank'), cell(5, 3, 'Approve marketing spend; cut SaaS costs'), cell(5, 4, '2025-01-25'),
  cell(6, 0, '2025-01-22'), cell(6, 1, 'Security review'), cell(6, 2, 'Charlie, Eve'), cell(6, 3, 'Enable 2FA; rotate API keys'), cell(6, 4, '2025-02-05'),
  cell(7, 0, '2025-01-27'), cell(7, 1, 'Sprint planning'), cell(7, 2, 'Full team'), cell(7, 3, 'Start CRM integration; 3 stories'), cell(7, 4, '2025-02-07'),
  cell(8, 0, '2025-01-29'), cell(8, 1, 'Design sync'), cell(8, 2, 'Diana, Frank'), cell(8, 3, 'Finalize new brand colors'), cell(8, 4, '2025-02-03'),
  cell(9, 0, '2025-01-31'), cell(9, 1, 'Month-end review'), cell(9, 2, 'Alice, Bob'), cell(9, 3, 'All January milestones met'), cell(9, 4, 'N/A'),
];

const meetingCellsRu = [
  hdr(0, 0, 'Дата'), hdr(0, 1, 'Тема'), hdr(0, 2, 'Участники'), hdr(0, 3, 'Решения'), hdr(0, 4, 'Срок'),
  cell(1, 0, '2025-01-06'), cell(1, 1, 'Планирование Q1'), cell(1, 2, 'Алиса, Борис, Сергей'), cell(1, 3, 'Утвердить OKR на Q1; нанять 2 инженеров'), cell(1, 4, '2025-01-20'),
  cell(2, 0, '2025-01-08'), cell(2, 1, 'Дорожная карта продукта'), cell(2, 2, 'Диана, Елена, Фёдор'), cell(2, 3, 'Приоритет — мобильное приложение v2'), cell(2, 4, '2025-01-15'),
  cell(3, 0, '2025-01-13'), cell(3, 1, 'Ревью спринта'), cell(3, 2, 'Вся команда'), cell(3, 3, 'Задеплоить модуль авторизации; исправить 12 багов'), cell(3, 4, '2025-01-17'),
  cell(4, 0, '2025-01-15'), cell(4, 1, 'Обратная связь от клиента'), cell(4, 2, 'Алиса, Мария'), cell(4, 3, 'Добавить экспорт в PDF; улучшить UX'), cell(4, 4, '2025-02-01'),
  cell(5, 0, '2025-01-20'), cell(5, 1, 'Обзор бюджета'), cell(5, 2, 'Борис, Николай'), cell(5, 3, 'Утвердить маркетинговый бюджет; сократить SaaS'), cell(5, 4, '2025-01-25'),
  cell(6, 0, '2025-01-22'), cell(6, 1, 'Обзор безопасности'), cell(6, 2, 'Сергей, Елена'), cell(6, 3, 'Включить 2FA; обновить API-ключи'), cell(6, 4, '2025-02-05'),
  cell(7, 0, '2025-01-27'), cell(7, 1, 'Планирование спринта'), cell(7, 2, 'Вся команда'), cell(7, 3, 'Начать интеграцию CRM; 3 задачи'), cell(7, 4, '2025-02-07'),
  cell(8, 0, '2025-01-29'), cell(8, 1, 'Синхронизация по дизайну'), cell(8, 2, 'Диана, Фёдор'), cell(8, 3, 'Утвердить новые фирменные цвета'), cell(8, 4, '2025-02-03'),
  cell(9, 0, '2025-01-31'), cell(9, 1, 'Итоги месяца'), cell(9, 2, 'Алиса, Борис'), cell(9, 3, 'Все январские вехи достигнуты'), cell(9, 4, 'Н/Д'),
];

/* ══════════════════════════════════════════════════════
   Templates Array
   ══════════════════════════════════════════════════════ */

export const templates: Template[] = [
  {
    slug: 'monthly-budget',
    icon: 'fas fa-wallet',
    category: 'finance',
    nameKey: 'templates.budget.name',
    descKey: 'templates.budget.desc',
    seoTitleKey: 'templates.budget.seo_title',
    seoDescKey: 'templates.budget.seo_desc',
    sheetData: sheet('Budget', budgetCells, [160, 100, 100, 100]),
    sheetDataRu: sheet('Бюджет', budgetCellsRu, [160, 100, 100, 100]),
  },
  {
    slug: 'weekly-schedule',
    icon: 'fas fa-calendar-week',
    category: 'planning',
    nameKey: 'templates.schedule.name',
    descKey: 'templates.schedule.desc',
    seoTitleKey: 'templates.schedule.seo_title',
    seoDescKey: 'templates.schedule.seo_desc',
    sheetData: sheet('Schedule', scheduleCells, [80, 120, 120, 120, 120, 120, 120, 120]),
    sheetDataRu: sheet('Расписание', scheduleCellsRu, [80, 140, 140, 140, 140, 140, 140, 140]),
  },
  {
    slug: 'gantt-chart',
    icon: 'fas fa-project-diagram',
    category: 'planning',
    nameKey: 'templates.gantt.name',
    descKey: 'templates.gantt.desc',
    seoTitleKey: 'templates.gantt.seo_title',
    seoDescKey: 'templates.gantt.seo_desc',
    sheetData: sheet('Gantt', ganttCells, [200, 110, 110, 110, 120]),
    sheetDataRu: sheet('Диаграмма Ганта', ganttCellsRu, [200, 110, 110, 110, 140]),
  },
  {
    slug: 'checklist',
    icon: 'fas fa-tasks',
    category: 'planning',
    nameKey: 'templates.checklist.name',
    descKey: 'templates.checklist.desc',
    seoTitleKey: 'templates.checklist.seo_title',
    seoDescKey: 'templates.checklist.seo_desc',
    sheetData: sheet('Checklist', checklistCells, [200, 120, 100, 110]),
    sheetDataRu: sheet('Чек-лист', checklistCellsRu, [260, 120, 100, 110]),
  },
  {
    slug: 'sales-report',
    icon: 'fas fa-chart-line',
    category: 'business',
    nameKey: 'templates.sales.name',
    descKey: 'templates.sales.desc',
    seoTitleKey: 'templates.sales.seo_title',
    seoDescKey: 'templates.sales.seo_desc',
    sheetData: sheet('Sales', salesCells, [110, 100, 100, 110]),
    sheetDataRu: sheet('Продажи', salesCellsRu, [110, 100, 100, 110]),
  },
  {
    slug: 'payroll',
    icon: 'fas fa-money-check-alt',
    category: 'hr',
    nameKey: 'templates.payroll.name',
    descKey: 'templates.payroll.desc',
    seoTitleKey: 'templates.payroll.seo_title',
    seoDescKey: 'templates.payroll.seo_desc',
    sheetData: sheet('Payroll', payrollCells, [150, 110, 90, 110, 110]),
    sheetDataRu: sheet('Зарплата', payrollCellsRu, [150, 110, 90, 110, 110]),
  },
  {
    slug: 'bookkeeping',
    icon: 'fas fa-book',
    category: 'finance',
    nameKey: 'templates.bookkeeping.name',
    descKey: 'templates.bookkeeping.desc',
    seoTitleKey: 'templates.bookkeeping.seo_title',
    seoDescKey: 'templates.bookkeeping.seo_desc',
    sheetData: sheet('Ledger', bookkeepingCells, [100, 220, 100, 100, 100]),
    sheetDataRu: sheet('Книга учёта', bookkeepingCellsRu, [100, 240, 100, 100, 100]),
  },
  {
    slug: 'pnl',
    icon: 'fas fa-balance-scale',
    category: 'finance',
    nameKey: 'templates.pnl.name',
    descKey: 'templates.pnl.desc',
    seoTitleKey: 'templates.pnl.seo_title',
    seoDescKey: 'templates.pnl.seo_desc',
    sheetData: sheet('P&L', pnlCells, [160, 100, 100, 100, 100, 110]),
    sheetDataRu: sheet('ОПиУ', pnlCellsRu, [160, 100, 100, 100, 100, 110]),
  },
  {
    slug: 'timesheet',
    icon: 'fas fa-clock',
    category: 'hr',
    nameKey: 'templates.timesheet.name',
    descKey: 'templates.timesheet.desc',
    seoTitleKey: 'templates.timesheet.seo_title',
    seoDescKey: 'templates.timesheet.seo_desc',
    sheetData: sheet('Timesheet', timesheetCells, [150, 70, 70, 70, 70, 70, 70, 70, 80]),
    sheetDataRu: sheet('Табель', timesheetCellsRu, [150, 70, 70, 70, 70, 70, 70, 70, 80]),
  },
  {
    slug: 'kpi',
    icon: 'fas fa-bullseye',
    category: 'hr',
    nameKey: 'templates.kpi.name',
    descKey: 'templates.kpi.desc',
    seoTitleKey: 'templates.kpi.seo_title',
    seoDescKey: 'templates.kpi.seo_desc',
    sheetData: sheet('KPI', kpiCells, [120, 140, 90, 90, 110]),
    sheetDataRu: sheet('KPI', kpiCellsRu, [120, 140, 90, 90, 120]),
  },
  {
    slug: 'inventory',
    icon: 'fas fa-warehouse',
    category: 'business',
    nameKey: 'templates.inventory.name',
    descKey: 'templates.inventory.desc',
    seoTitleKey: 'templates.inventory.seo_title',
    seoDescKey: 'templates.inventory.seo_desc',
    sheetData: sheet('Inventory', inventoryCells, [90, 170, 70, 100, 110]),
    sheetDataRu: sheet('Склад', inventoryCellsRu, [90, 200, 70, 100, 110]),
  },
  {
    slug: 'client-list',
    icon: 'fas fa-address-book',
    category: 'marketing',
    nameKey: 'templates.clients.name',
    descKey: 'templates.clients.desc',
    seoTitleKey: 'templates.clients.seo_title',
    seoDescKey: 'templates.clients.seo_desc',
    sheetData: sheet('Clients', clientCells, [160, 160, 130, 180, 90]),
    sheetDataRu: sheet('Клиенты', clientCellsRu, [160, 160, 150, 180, 100]),
  },
  {
    slug: 'project-tracker',
    icon: 'fas fa-clipboard-list',
    category: 'business',
    nameKey: 'templates.project.name',
    descKey: 'templates.project.desc',
    seoTitleKey: 'templates.project.seo_title',
    seoDescKey: 'templates.project.seo_desc',
    sheetData: sheet('Projects', projectCells, [180, 110, 100, 100, 110]),
    sheetDataRu: sheet('Проекты', projectCellsRu, [200, 130, 100, 100, 110]),
  },
  {
    slug: 'expense-report',
    icon: 'fas fa-receipt',
    category: 'finance',
    nameKey: 'templates.expense.name',
    descKey: 'templates.expense.desc',
    seoTitleKey: 'templates.expense.seo_title',
    seoDescKey: 'templates.expense.seo_desc',
    sheetData: sheet('Expenses', expenseCells, [100, 180, 130, 100, 80]),
    sheetDataRu: sheet('Расходы', expenseCellsRu, [100, 200, 160, 100, 80]),
  },
  {
    slug: 'meeting-notes',
    icon: 'fas fa-users',
    category: 'business',
    nameKey: 'templates.meeting.name',
    descKey: 'templates.meeting.desc',
    seoTitleKey: 'templates.meeting.seo_title',
    seoDescKey: 'templates.meeting.seo_desc',
    sheetData: sheet('Meetings', meetingCells, [100, 150, 170, 250, 100]),
    sheetDataRu: sheet('Протоколы', meetingCellsRu, [100, 200, 190, 300, 100]),
  },
];

export const categories = ['all', 'finance', 'planning', 'hr', 'business', 'marketing'] as const;

export function getTemplateBySlug(slug: string): Template | undefined {
  return templates.find((t) => t.slug === slug);
}

/**
 * Returns templates with sheetData set to the appropriate language version.
 * For 'ru', uses sheetDataRu (falling back to sheetData if not available).
 * For any other language, uses sheetData (English).
 */
export function getLocalizedTemplates(lang: string): Template[] {
  if (lang === 'ru') {
    return templates.map((t) => ({
      ...t,
      sheetData: t.sheetDataRu || t.sheetData,
    }));
  }
  return templates;
}
